import { ACTIONS } from '@sravni/ugc/lib/specialOffers/lists';
import type { ActionsType, Tab } from '@sravni/ugc/lib/specialOffers/types';
import pluralize from '@sravni/utils/lib/pluralize';

import { END_TIME_STATUS } from '../constants/actions';
import type { IExtendedOrderOsago } from '../interfaces/orders';

interface IActionDataParams {
  tab: Tab<ActionsType>;
  item: IExtendedOrderOsago;
}

const months = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const getButtonText = ({ status }): string => {
  if (status === 'pending') return 'Продолжить расчёт';

  if (status === 'paid') {
    return 'Продлить полис';
  }
};

const getDateStatus = (endTimeStatus: END_TIME_STATUS, endDateDifference: number) => {
  if (endTimeStatus === END_TIME_STATUS.ended) {
    return 'Закончился';
  }

  if ([END_TIME_STATUS.endedRecently, END_TIME_STATUS.endedRecentlyProlongation].includes(endTimeStatus)) {
    return `Закончился ${endDateDifference} ${pluralize(endDateDifference, 'день', 'дня', 'дней')} назад`;
  }

  if ([END_TIME_STATUS.endingToday, END_TIME_STATUS.endingTodayProlongation].includes(endTimeStatus)) {
    return `Заканчивается сегодня`;
  }

  if ([END_TIME_STATUS.endingProlongation, END_TIME_STATUS.ending, END_TIME_STATUS.active].includes(endTimeStatus)) {
    return `${pluralize(endDateDifference, 'Остался', 'Осталось', 'Осталось')} ${endDateDifference} ${pluralize(
      endDateDifference,
      'день',
      'дня',
      'дней',
    )} действия`;
  }

  return 'Подставить';
};

const generateRandomOsagoSearchId = (): string =>
  [0, 0, 0, 0, 0, 0, 0].reduce((acc) => `${acc}${(Math.random() * 10).toFixed(0)}`, '').substring(0, 7);

const getOsagoLink = ({ externalId, isImported }: IExtendedOrderOsago) =>
  isImported
    ? `/osago/?hash=${externalId}&searchId=${generateRandomOsagoSearchId()}`
    : `/osago/?orderHash=${externalId}`;

const getActionDescription = ({ status }: IExtendedOrderOsago): string => {
  if (status === 'paid') {
    return 'Ваш полис ОСАГО';
  }

  if (status === 'pending') {
    return 'Расчёт полиса ОСАГО';
  }
};

const getActionDates = ({ status, end, start }: IExtendedOrderOsago) => {
  const endDate = new Date(end);

  const endLabel = (
    <>
      {endDate.getDate()}
      {'\u00A0'}
      {months[endDate.getMonth()]}
      {'\u00A0'}
      {endDate.getFullYear()}
    </>
  );

  if (status === 'paid') {
    return (
      <>
        до{'\u00A0'}
        {endLabel}
      </>
    );
  }

  if (status === 'pending') {
    const startDate = new Date(start);
    const startLabel = (
      <>
        c{'\u00A0'}
        {startDate.getDate()}
        {'\u00A0'}
        {months[startDate.getMonth()]}
        {'\u00A0'}
        {startDate.getFullYear()}
      </>
    );

    return startLabel;
  }
};

const getActionDatesDescription = ({ status, endDateDifference, endTimeStatus }: IExtendedOrderOsago) => {
  if (status === 'paid') {
    return getDateStatus(endTimeStatus, Math.abs(endDateDifference));
  }

  if (status === 'pending') {
    return null;
  }
};

const getActionsDatesTitle = ({ status, endTimeStatus }: IExtendedOrderOsago) => {
  if (status === 'paid') {
    if (
      [
        END_TIME_STATUS.endingProlongation,
        END_TIME_STATUS.ending,
        END_TIME_STATUS.endingTodayProlongation,
        END_TIME_STATUS.endingToday,
        END_TIME_STATUS.active,
      ].includes(endTimeStatus)
    ) {
      return 'Действует';
    }

    return 'Действовал';
  }

  if (status === 'pending') {
    return 'Будет действовать';
  }
};

const getIsPrimaryButton = ({ status }: IExtendedOrderOsago): boolean => status === 'paid';

const getIsPrimaryDate = (item: IExtendedOrderOsago): boolean => {
  const { status } = item;

  if (status === 'paid') {
    return getIsPrimaryButton(item);
  }

  if (status === 'pending') {
    return false;
  }
};

export const useActionData = ({ tab, item }: IActionDataParams) => {
  if (tab.type === ACTIONS.osago) {
    const { carModelName, organization, status } = item;

    return {
      actionDescription: getActionDescription(item),
      actionTitle: carModelName,
      actionDates: getActionDates(item),
      actionDatesTitle: getActionsDatesTitle(item),
      actionDatesDescription: getActionDatesDescription(item),
      actionButton: getButtonText(item),
      isPrimaryDate: getIsPrimaryDate(item),
      isPrimaryButton: getIsPrimaryButton(item),
      logoSrc: `//f.sravni.ru/logotypes/ic/sq/logo_${organization?.oldId}.svg`,
      companyName: organization?.name,
      clickUrl: getOsagoLink(item),
      advertisingCategory: 'ОСАГО',
      clickAdvertisingAction: `Кнопка|${getButtonText(item)}`,
      viewAdvertisingAction: `Показ|${getButtonText(item)}`,
      advertisingLabel: 'Ваши действия',
      isPaid: status === 'paid',
      isShowOrganization: status === 'paid',
    };
  }
};
